import { normalizeTicketGame, type TicketSource } from "./lotto-ticket-engine";

export interface LocalLottoTicket {
  readonly schemaVersion: 1;
  readonly ticketId: string;
  readonly drawNo: number | null;
  readonly source: TicketSource;
  readonly games: readonly (readonly number[])[];
  readonly createdAtEpochMillis: number;
  readonly updatedAtEpochMillis: number;
}

const DATABASE_NAME = "appforge-lotto-map-web-vault";
const STORE_NAME = "tickets";
const DATABASE_VERSION = 1;

/** Browser-local only. Never add a network fallback for tickets or QR payloads. */
export async function listLocalTickets(): Promise<readonly LocalLottoTicket[]> {
  const database = await openVault();
  try {
    const values = await request<unknown[]>(database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll());
    return values.filter(isLocalTicket).sort((left, right) => right.updatedAtEpochMillis - left.updatedAtEpochMillis);
  } finally { database.close(); }
}

export async function saveLocalTicket(ticket: LocalLottoTicket): Promise<void> {
  validateTicket(ticket);
  const database = await openVault();
  try { await transactionComplete(database.transaction(STORE_NAME, "readwrite"), (store) => store.put(ticket)); } finally { database.close(); }
}

export async function removeLocalTicket(ticketId: string): Promise<void> {
  if (!ticketId || ticketId.length > 80) throw new Error("invalid_ticket_id");
  const database = await openVault();
  try { await transactionComplete(database.transaction(STORE_NAME, "readwrite"), (store) => store.delete(ticketId)); } finally { database.close(); }
}

export function createLocalTicket(drawNo: number | null, source: TicketSource, games: readonly (readonly number[])[]): LocalLottoTicket {
  const now = Date.now();
  const ticket: LocalLottoTicket = {
    schemaVersion: 1,
    ticketId: ticketId(),
    drawNo,
    source,
    games: games.map((game) => normalizeTicketGame(game)),
    createdAtEpochMillis: now,
    updatedAtEpochMillis: now,
  };
  validateTicket(ticket);
  return ticket;
}

function openVault(): Promise<IDBDatabase> {
  if (!globalThis.indexedDB) return Promise.reject(new Error("indexeddb_unavailable"));
  return new Promise((resolve, reject) => {
    const open = globalThis.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    open.onupgradeneeded = () => {
      const database = open.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME, { keyPath: "ticketId" });
    };
    open.onerror = () => reject(new Error("ticket_vault_open_failed"));
    open.onsuccess = () => resolve(open.result);
  });
}

function request<T>(operation: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(new Error("ticket_vault_request_failed"));
  });
}

function transactionComplete(transaction: IDBTransaction, operation: (store: IDBObjectStore) => IDBRequest): Promise<void> {
  return new Promise((resolve, reject) => {
    let requestFailed = false;
    const operationRequest = operation(transaction.objectStore(STORE_NAME));
    operationRequest.onerror = () => { requestFailed = true; };
    transaction.oncomplete = () => requestFailed ? reject(new Error("ticket_vault_write_failed")) : resolve();
    transaction.onerror = () => reject(new Error("ticket_vault_write_failed"));
    transaction.onabort = () => reject(new Error("ticket_vault_write_failed"));
  });
}

function isLocalTicket(value: unknown): value is LocalLottoTicket {
  try { validateTicket(value as LocalLottoTicket); return true; } catch { return false; }
}

function validateTicket(ticket: LocalLottoTicket): void {
  if (ticket.schemaVersion !== 1 || !ticket.ticketId || ticket.ticketId.length > 80) throw new Error("invalid_ticket");
  if (ticket.drawNo !== null && (!Number.isInteger(ticket.drawNo) || ticket.drawNo <= 0)) throw new Error("invalid_ticket_draw");
  if (ticket.source !== "QR" && ticket.source !== "MANUAL" && ticket.source !== "GENERATED") throw new Error("invalid_ticket_source");
  if (!Array.isArray(ticket.games) || !ticket.games.length || ticket.games.length > 5) throw new Error("invalid_ticket_games");
  ticket.games.forEach(normalizeTicketGame);
  if (!Number.isSafeInteger(ticket.createdAtEpochMillis) || !Number.isSafeInteger(ticket.updatedAtEpochMillis)) throw new Error("invalid_ticket_timestamp");
}

function ticketId(): string {
  if (globalThis.crypto?.randomUUID) return `web-${globalThis.crypto.randomUUID()}`;
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}
